<template>
	<PageTitleComponent />

	<div class="form-container">
		<form
			action="#"
			@submit.prevent="handlePasswordReset"
		>
			<h2>Reset Your Password</h2>

			<p>Enter your <strong>personal school email address</strong> to receive a password reset link.</p>

			<div
				class="reset-content"
				v-if="success === null"
			>
				<div class="fields">
					<div class="field">
						<input
							name="email"
							autocomplete="email"
							v-model="email"
							type="email"
							placeholder="Email Address"
							required
						/>
					</div>
				</div>
				<button
					class="btn btn-primary"
					type="submit"
					:disabled="!isFormValid || loading"
				>
					{{ loading ? 'Sending...' : 'Send Reset Email' }}
				</button>
			</div>
			<div
				class="error-container"
				v-else-if="success === false"
			>
				<div class="error">There was an error processing your request. Please try again or contact support if the issue persists.</div>
			</div>
			<div
				class="success-container"
				v-else
			>
				<div
					class="success"
					v-if="success"
				>
					{{ message }}
				</div>
			</div>

			<div class="login-link">
				<small>
					<router-link
						:to="{
							name: 'login',
						}"
						>Back to Login</router-link
					>
				</small>
			</div>

			<LoadingComponent :loading="loading" />
		</form>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import PageTitleComponent from '@/components/shared/PageTitleComponent.vue';
import { useUser } from '@/stores/userStore';
import { mapActions } from 'pinia';
import LoadingComponent from '@/components/shared/LoadingComponent.vue';

export default defineComponent({
	components: { PageTitleComponent, LoadingComponent },
	data() {
		return {
			email: '',
			success: null as boolean | null,
			loading: false,
			message: 'If your account exists, a password reset email has been sent to your email address. Please check your inbox and follow the instructions.',
		};
	},
	computed: {
		isFormValid(): boolean {
			return this.email.length > 0;
		},
	},
	methods: {
		async handlePasswordReset() {
			this.loading = true;

			const res = await this.requestPasswordReset(this.email);
			this.success = res.success;

			if (res.message) {
				this.message = res.message;
			}

			this.loading = false;
		},
		...mapActions(useUser, ['requestPasswordReset']),
	},
});
</script>

<style scoped lang="scss">
.form-container {
	display: flex;
	flex: 1;
	justify-content: center;
	align-items: center;
	margin-bottom: 100px;
}

form {
	background-color: rgb(var(--rs-color-brand-navy));
	padding: var(--rs-layout-content-padding);
	width: clamp(200px, 400px, 100%);

	h2 {
		margin-bottom: 10px;
		color: white;
		font-size: 24px;
	}

	p {
		margin-bottom: 30px;
		color: white;
		font-size: 14px;
	}

	.fields {
		.field {
			margin-bottom: 20px;
		}
	}

	.error-container {
		padding-bottom: 20px;

		.error {
			color: #ff6b6b;
		}
	}

	.success-container {
		padding-bottom: 20px;

		.success {
			color: white;
		}
	}

	.login-link {
		margin-top: 20px;

		a {
			color: white;
			text-decoration: none;

			&:hover {
				text-decoration: underline;
			}
		}
	}

	button:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
}
</style>
